import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CARD_SLIDER_CSS, cardSliderMetrics, resolveScaledFontPx } from '../constants/cardSliderWidget';

function stop(event) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Palco do carrossel de cards. A mesma conta de largura vale na visualização.
 */
export default function RenderCardSlider({ content = {} }) {
  const viewportRef = useRef(null);
  const [box, setBox] = useState({ width: 0, mobile: false });
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const cards = Array.isArray(content.cards) ? content.cards.slice(0, 12) : [];
  const metrics = cardSliderMetrics(content, box.width, page, box.mobile);
  const layout = content.layout === 'centered' ? 'centered' : 'netflix';

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const canvas = viewport?.closest('.course-wrapper');
    const measure = () => {
      setBox({
        width: viewport ? viewport.clientWidth : 0,
        mobile: !!canvas?.classList.contains('geppetto-edit-bp-mobile'),
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (viewport) observer.observe(viewport);
    const classObserver = canvas ? new MutationObserver(measure) : null;
    classObserver?.observe(canvas, { attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      classObserver?.disconnect();
    };
  }, [cards.length, layout, content.perPage, content.perPageMobile]);

  useEffect(() => {
    if (!content.autoplay || paused || metrics.starts.length < 2) return undefined;
    const ms = Math.max(1200, parseInt(content.autoplayMs, 10) || 4000);
    const timer = setInterval(() => {
      setPage((current) => {
        if (content.loop === false && current >= metrics.starts.length - 1) return current;
        return current + 1;
      });
    }, ms);
    return () => clearInterval(timer);
  }, [content.autoplay, content.autoplayMs, content.loop, metrics.starts.length, paused]);

  const go = (delta) => {
    const total = metrics.starts.length;
    if (total < 2) return;
    setPage((current) => {
      const next = current + delta;
      if (content.loop === false) return Math.max(0, Math.min(total - 1, next));
      return next;
    });
  };

  const vars = {
    '--cs-card': content.cardBg || '#ffffff',
    '--cs-title': content.titleColor || '#0f172a',
    '--cs-text': content.textColor || '#475569',
    '--cs-title-size': `${resolveScaledFontPx(content, 'cardTitleFontPx', 16, 1)}px`,
    '--cs-text-size': `${resolveScaledFontPx(content, 'cardTextFontPx', 14, 0.875)}px`,
    '--cs-btn': content.btnBg || '#2563eb',
    '--cs-btn-text': content.btnColor || '#ffffff',
    '--cs-arrow': content.arrowBg || '#ffffff',
    '--cs-arrow-text': content.arrowColor || '#0f172a',
    '--cs-dot': content.dotColor || '#cbd5e1',
    '--cs-dot-on': content.dotActive || '#2563eb',
    '--cs-radius': `${Math.max(0, Math.min(32, parseInt(content.radius, 10) || 14))}px`,
    '--cs-img': `${Math.max(80, Math.min(420, parseInt(content.imageHeight, 10) || 160))}px`,
  };
  const classes = [
    'madri-card-slider',
    layout === 'centered' ? 'madri-card-slider--centered' : 'madri-card-slider--netflix',
    content.cardAlign === 'center' ? 'madri-card-slider--center-text' : '',
    content.shadow === false ? '' : 'madri-card-slider--shadow',
    content.hoverLift === false ? '' : 'madri-card-slider--lift',
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} style={vars} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <style>{CARD_SLIDER_CSS}</style>
      <div
        className="madri-card-slider__viewport"
        ref={viewportRef}
        onPointerUp={(event) => {
          const start = event.currentTarget.dataset.x;
          if (start == null || event.target.closest('a,button')) return;
          const dx = event.clientX - Number(start);
          if (dx > 48) go(-1);
          else if (dx < -48) go(1);
        }}
        onPointerDown={(event) => {
          if (event.target.closest('a,button')) return;
          event.currentTarget.dataset.x = String(event.clientX);
        }}
      >
        <div
          className="madri-card-slider__track"
          style={{ gap: metrics.gap, transform: `translateX(${box.width ? metrics.x : 0}px)` }}
        >
          {cards.map((card, index) => (
            <SliderCard key={`${card.title || 'card'}-${index}`} card={card} width={box.width ? metrics.cardW : undefined} />
          ))}
        </div>
      </div>
      {content.showArrows !== false && metrics.starts.length > 1 && (
        <>
          <button type="button" className="madri-card-slider__arrow madri-card-slider__arrow--prev" aria-label="Anterior" disabled={content.loop === false && metrics.page <= 0} onClick={(event) => { stop(event); go(-1); }}>‹</button>
          <button type="button" className="madri-card-slider__arrow madri-card-slider__arrow--next" aria-label="Próximo" disabled={content.loop === false && metrics.page >= metrics.starts.length - 1} onClick={(event) => { stop(event); go(1); }}>›</button>
        </>
      )}
      {content.showDots !== false && metrics.starts.length > 1 && (
        <div className="madri-card-slider__dots">
          {metrics.starts.map((start, index) => (
            <button
              key={start}
              type="button"
              className={`madri-card-slider__dot${index === metrics.page ? ' is-active' : ''}`}
              aria-label={String(index + 1)}
              onClick={(event) => { stop(event); setPage(index); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SliderCard({ card, width }) {
  const title = String(card?.title || '').trim();
  const text = String(card?.text || '').trim();
  const button = String(card?.buttonLabel || '').trim();
  const href = String(card?.href || '').trim();
  const image = String(card?.imageUrl || '').trim();
  const style = width ? { flex: `0 0 ${width}px`, width } : undefined;
  const media = image
    ? <img className="madri-card-slider__media" src={image} alt={title || ''} />
    : <div className="madri-card-slider__ph" />;
  const body = (title || text || button) ? (
    <div className="madri-card-slider__body">
      {title ? <div className="madri-card-slider__title">{title}</div> : null}
      {text ? <div className="madri-card-slider__text">{text}</div> : null}
      {button ? <a className="madri-card-slider__btn" href={href || '#'} onClick={stop}>{button}</a> : null}
    </div>
  ) : null;
  if (!button && href) {
    return <a className="madri-card-slider__card" href={href} style={style} onClick={stop}>{media}{body}</a>;
  }
  return <div className="madri-card-slider__card" style={style}>{media}{body}</div>;
}
